import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, LogOut, Phone, RefreshCw, Sparkles, Wrench } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STATUSES = ["Alındı", "İnceleniyor", "Planlandı", "Tamamlandı"];

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#F8FAFC" d="M21.35 11.1H12v2.9h5.35c-.5 2.4-2.6 4-5.35 4a6 6 0 1 1 0-12c1.5 0 2.9.55 3.95 1.45l2.2-2.2A9.4 9.4 0 1 0 12 21.4c5.4 0 9-3.8 9-9.15 0-.4-.05-.8-.15-1.15Z" /></svg>
);

const statusClass = (status) => `status-chip st-${Math.max(0, STATUSES.indexOf(status))}`;

const RequestCard = ({ r, admin, edits, setEdits, onSave }) => (
  <article className="req-card" data-testid={`request-card-${r.id}`}>
    <div className="req-card-top">
      <div>
        <strong>{r.name}</strong>
        <span className="req-service">{r.service}</span>
      </div>
      <span className={statusClass(r.status)} data-testid={`request-status-${r.id}`}>{r.status}</span>
    </div>
    <div className="req-meta">
      <span><Phone size={13} /> {r.phone}</span>
      {r.mahalle && <span>{r.mahalle}</span>}
      {r.preferred_time && <span>{r.preferred_time}</span>}
      <span>{r.created_at ? new Date(r.created_at).toLocaleString("tr-TR") : ""}</span>
    </div>
    {r.note && <p className="req-note">{r.note}</p>}
    {admin && (
      <div className="req-admin-row">
        <select
          data-testid={`request-status-select-${r.id}`}
          value={edits[r.id] ?? r.status}
          onChange={(e) => setEdits((s) => ({ ...s, [r.id]: e.target.value }))}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid={`request-status-save-${r.id}`} onClick={() => onSave(r.id)}>Kaydet</button>
      </div>
    )}
  </article>
);

export default function Dashboard() {
  const { user, loading, login, logout } = useAuth();
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const res = await axios.get(`${API}/service-requests/mine`, { withCredentials: true });
      setMine(res.data);
    } catch { /* panel yüklenirken sessiz geç */ }
    setFetching(false);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/service-requests`, { withCredentials: true });
      setAll(res.data);
    } catch { /* admin değilse boş kalır */ }
  }, []);

  useEffect(() => {
    if (user) {
      load();
      if (user.is_admin) loadAll();
    }
  }, [user, load, loadAll]);

  const saveStatus = async (id) => {
    try {
      await axios.patch(`${API}/service-requests/${id}`, { status: edits[id] }, { withCredentials: true });
      toast.success("Talep durumu güncellendi.");
      loadAll();
    } catch {
      toast.error("Durum güncellenemedi.");
    }
  };

  const getSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/service-requests/summary`, { withCredentials: true });
      setSummary(res.data.summary);
    } catch {
      toast.error("AI özeti alınamadı.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Çıkış yapıldı.");
  };

  if (loading) {
    return (
      <div className="auth-callback" data-testid="dashboard-loading">
        <div className="auth-callback-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dash-gate" data-testid="dashboard-login-gate">
        <div className="dash-gate-card">
          <span className="brand-mark"><Wrench size={18} /></span>
          <h1>Panel için giriş gerekli</h1>
          <p>Servis taleplerinizi görüntülemek için Google hesabınızla giriş yapın.</p>
          <button className="btn-login big" data-testid="dashboard-login-button" onClick={login}>
            <GoogleIcon /> Google ile Giriş Yap
          </button>
          <Link to="/" className="dash-back" data-testid="gate-back-home-link"><ArrowLeft size={15} /> Siteye dön</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-shell" data-testid="customer-dashboard">
      <header className="dash-header">
        <Link to="/" className="brand" data-testid="dashboard-home-link">
          <img className="brand-logo" src="/logo.png" alt="Nova Teknik Servis Düzce" />
        </Link>
        <div className="dash-header-actions">
          <Link to="/" className="dash-back" data-testid="dashboard-back-link"><ArrowLeft size={15} /> Site</Link>
          <button className="dash-logout" data-testid="dashboard-logout-button" onClick={handleLogout}><LogOut size={15} /> Çıkış</button>
        </div>
      </header>

      <main className="dash-container">
        <div className="success-banner" data-testid="login-success-banner">
          <CheckCircle2 size={22} />
          <div>
            <strong>Giriş başarılı</strong>
            <span>Hoş geldiniz, {user.name || user.email}</span>
          </div>
          {user.is_admin && <span className="admin-badge" data-testid="admin-badge">PERSONEL</span>}
        </div>

        <section className="dash-section">
          <div className="dash-section-head">
            <h2>{user.is_admin ? "Tüm Servis Talepleri" : "Servis Taleplerim"}</h2>
            <button className="dash-refresh" data-testid="dashboard-refresh-button" onClick={() => { load(); if (user.is_admin) loadAll(); }} disabled={fetching}>
              <RefreshCw size={14} /> Yenile
            </button>
          </div>

          {user.is_admin && (
            <div className="admin-summary-box" data-testid="admin-summary-box">
              <button className="btn-primary slim" data-testid="admin-summary-button" onClick={getSummary} disabled={summaryLoading}>
                <Sparkles size={15} /> {summaryLoading ? "AI özetliyor..." : "AI ile Talepleri Özetle"}
              </button>
              {summary && <p className="admin-summary-text" data-testid="admin-summary-result">{summary}</p>}
            </div>
          )}

          <div className="req-list" data-testid="request-list">
            {(user.is_admin ? all : mine).length === 0 && !fetching && (
              <p className="req-empty" data-testid="request-list-empty">
                {user.is_admin ? "Henüz servis talebi yok." : "Henüz giriş yaptığınız hesaba bağlı servis talebi yok. Siteden form doldurduğunuzda talepleriniz burada listelenir."}
              </p>
            )}
            {(user.is_admin ? all : mine).map((r) => (
              <RequestCard key={r.id} r={r} admin={user.is_admin} edits={edits} setEdits={setEdits} onSave={saveStatus} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
